import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { LGoButton } from "@/ui/branded";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/primitives/collapsible";
import type { ConfirmationDeadlineStatus, DeadlineUrgency } from "@/domain/reservation/rules";
import { formatDate, formatDateTime, formatTime } from "@/lib/time";

interface ConfirmationDeadlineProps {
  deadline: Date;
  status: ConfirmationDeadlineStatus;
}

function absoluteLine(deadline: Date, urgency: DeadlineUrgency): string {
  const time = formatTime(deadline);
  switch (urgency) {
    case "lapsed":
      return "Lhůta na potvrzení uplynula";
    case "nextDay":
      return `Potvrďte do zítra ${time}`;
    case "later":
      return `Potvrďte do ${formatDate(deadline, "EEEE d. M.")} ${time}`;
    default:
      return `Potvrďte do ${time}`;
  }
}

/** The consequence is stated once, inside the rule below — a row that repeats it
 *  next to every countdown reads as a warning about this booking rather than the
 *  standing rule it is. */
function remainingLine(urgency: DeadlineUrgency, minutesRemaining: number): string | null {
  if (urgency === "lapsed") return null;
  if (urgency === "lastHour") return "zbývá méně než hodina";
  const hours = Math.floor(minutesRemaining / 60);
  const minutes = minutesRemaining % 60;
  return minutes === 0 ? `zbývá ${hours} h` : `zbývá ${hours} h ${minutes} min`;
}

/** The window is `CONFIRMATION_WINDOW_HOURS` in lastgo-api — a platform rule no
 *  business can set, so the portal states it instead of offering a field. The
 *  cap belongs in the same breath: sixteen hours alone is wrong for an activity
 *  three hours away, which is the case a partner hits and disbelieves. The night
 *  is worth saying because it turns a lapsed booking from „the portal is
 *  arbitrary" into „I did not look for sixteen hours". */
const confirmationWindowRule =
  "Na potvrzení máte 16 hodin od objednání, nejpozději však 30 minut před začátkem termínu. " +
  "Při objednávce později než 30 minut před začátkem rezervaci potvrďte nebo odmítněte co nejdřív, vždy však před začátkem. " +
  "Lhůta záměrně přesahuje noc – rezervaci objednanou večer tak můžete potvrdit až ráno.";

const confirmationConsequence =
  "Když neodpovíte včas, rezervace se sama zruší, nic se zákazníkovi nestrhne a jeho částka se uvolní.";

interface ConfirmationWindowRuleProps {
  /** The still-waiting row states this once, in the disclosure, instead of
   *  next to the countdown (ticket 40). A row for a booking that already
   *  lapsed is past that decision, so it stays with the rule alone. */
  includeConsequence?: boolean;
}

export function ConfirmationWindowRule({
  includeConsequence = false,
}: ConfirmationWindowRuleProps = {}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <LGoButton
          variant="link"
          size="sm"
          className="h-auto px-0 text-xs text-muted-foreground"
          iconRight={open ? <ChevronUp /> : <ChevronDown />}
        >
          Jak lhůta funguje
        </LGoButton>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="max-w-prose text-xs text-muted-foreground">
          {includeConsequence
            ? `${confirmationWindowRule} ${confirmationConsequence}`
            : confirmationWindowRule}
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface LapsedConfirmationTimelineProps {
  /** Null when the payload carried no instant — never a guessed one. */
  orderedAt: Date | null;
  deadline: Date;
}

/** What a partner needs when the customer rings about a booking that vanished:
 *  the two instants the portal was given, and the rule that connects them. */
export function LapsedConfirmationTimeline({
  orderedAt,
  deadline,
}: LapsedConfirmationTimelineProps) {
  return (
    <div className="space-y-0.5">
      <p className="text-sm text-muted-foreground">
        {orderedAt ? `Objednáno ${formatDateTime(orderedAt)} · ` : ""}
        {`Lhůta uplynula ${formatDateTime(deadline)}`}
      </p>
      <ConfirmationWindowRule />
    </div>
  );
}

/**
 * The absolute instant first, the remainder second and smaller. Deliberately
 * not a countdown: over a 16-hour window a ticking clock looks urgent all day
 * and is ignored by the time it matters.
 */
export function ConfirmationDeadline({ deadline, status }: ConfirmationDeadlineProps) {
  const { urgency, minutesRemaining } = status;
  const pressing = urgency === "lastHour" || urgency === "lapsed";
  const remaining = remainingLine(urgency, minutesRemaining);

  return (
    <div className="space-y-0.5">
      <p
        className={
          pressing
            ? "text-sm font-semibold text-destructive"
            : "text-sm font-medium text-foreground"
        }
      >
        {absoluteLine(deadline, urgency)}
      </p>
      {remaining && <p className="text-xs text-muted-foreground">{remaining}</p>}
      <ConfirmationWindowRule includeConsequence />
    </div>
  );
}
